import React, {useState} from "react";
import {Box, Checkbox, ActionIcon, Fieldset, Tooltip} from "@mantine/core";
import {IDirConfig, UploaderPreset} from "@/protocols/types/protocolDefinitions";
import {getPresetDefaults} from "../constants";
import {DirectoryInput} from "./DirectoryInput";
import {PresetSelector} from "./PresetSelector";
import {ExtensionInputs} from "./ExtensionInputs";
import {IconTrash} from "@tabler/icons-react";
import classes from "./DirConfigRow.module.css";

interface DirConfigRowProps {
    config: IDirConfig;
    onUpdate: (updates: Partial<IDirConfig>) => void;
    onRemove?: () => void;
    showRemove: boolean;
    disabled?: boolean;
    error?: string;
}

const getInitialExtensions = (cfg: IDirConfig): string[] => {
    if (cfg.uploader_preset && cfg.uploader_preset !== "custom") {
        return getPresetDefaults(cfg.uploader_preset).extensions;
    }
    return cfg.uploader_custom_extensions?.map(e => e.extension) || [];
};

const getInitialMinAge = (cfg: IDirConfig): number => {
    if (cfg.uploader_preset && cfg.uploader_preset !== "custom") {
        return getPresetDefaults(cfg.uploader_preset).min_file_age;
    }
    return cfg.uploader_custom_extensions?.[0]?.min_file_age || 0;
};

const getInitialAfterUpload = (cfg: IDirConfig): "delete" | "backup" => {
    // Legacy protocol metadata may omit after_upload; treat missing as unchecked in UI.
    return cfg.after_upload ?? "delete";
};

export const DirConfigRow: React.FC<DirConfigRowProps> = ({
                                                              config,
                                                              onUpdate,
                                                              onRemove,
                                                              showRemove,
                                                              disabled,
                                                              error,
                                                          }) => {
    const [customExts, setCustomExts] = useState<string[]>(getInitialExtensions(config));
    const [customAge, setCustomAge] = useState<number>(getInitialMinAge(config));
    const [afterUpload, setAfterUpload] = useState<"delete" | "backup">(getInitialAfterUpload(config))

    const handlePresetChange = (value: UploaderPreset | "custom") => {
        if (value === "custom") {
            // Switching to custom: keep existing extensions with current min_age
            onUpdate({
                uploader_preset: "custom",
                uploader_custom_extensions: customExts.map(ext => ({extension: ext, min_file_age: customAge}))
            });
        } else {
            const defaults = getPresetDefaults(value);
            setCustomExts(defaults.extensions);
            setCustomAge(defaults.min_file_age);
            onUpdate({
                uploader_preset: value,
                uploader_custom_extensions: undefined
            });
        }
    };

    const handleExtensionsChange = (value: string[]) => {
        // Build updated extensions list: existing keep min_age, new get customAge
        const updatedExtensions = value.map(ext => {
            const existingEntry = config.uploader_custom_extensions?.find(e => e.extension === ext);
            return {
                extension: ext,
                min_file_age: existingEntry?.min_file_age ?? customAge
            };
        });

        setCustomExts(value);
        onUpdate({
            uploader_preset: "custom",
            uploader_custom_extensions: updatedExtensions
        });
    };

    const handleMinAgeChange = (value: number) => {
        setCustomAge(value);
        // Update min_age for ALL current extensions
        onUpdate({
            uploader_preset: "custom",
            uploader_custom_extensions: customExts.map(ext => ({extension: ext, min_file_age: value}))
        });
    };
    const handleAfterUploadChange = (value: "delete" | "backup") => {
        setAfterUpload(value);
        // Update after_upload for ALL extensions
        onUpdate({
            after_upload: value
        });
    };

    return (
        <Box className={classes.root}>
            {showRemove && onRemove ? (
                <ActionIcon
                    style={{alignSelf: "start"}}
                    size="md"
                    color="red"
                    variant="subtle"
                    onClick={onRemove}
                    disabled={disabled}
                >
                    <IconTrash size={"1rem"}/>
                </ActionIcon>
            ) : <Box/>}
            <DirectoryInput
                dirPath={config.dir_path}
                onUpdate={onUpdate}
                disabled={disabled}
                error={error}
                mb={"0.25rem"}
            />

            <Fieldset bg={"transparent"} bd={"none"} p={"0.25rem"}
                      style={{display: "flex", flexDirection: "row", gap: "1rem"}}>
                <Tooltip label={"Monitor all subdirectories in addition to specified directory"}>
                    <Checkbox
                        label={<Tooltip
                            label={"Monitor all subdirectories in addition to specified directory"}><Box>Recursive</Box></Tooltip>}
                        checked={config.recursive}
                        onChange={(e) => onUpdate({recursive: e.currentTarget.checked})}
                        disabled={disabled}
                        size="xs"
                    />
                </Tooltip>
                <Tooltip label={'After upload, move the file to a subfolder named "backup".'}>
                    <Checkbox label={<Tooltip
                        label={'After upload, move the file to a subfolder named "backup".'}><Box>Backup Files</Box></Tooltip>}
                              checked={afterUpload === "backup"}
                              size={"xs"}
                              disabled={disabled}
                              onChange={(ev) => handleAfterUploadChange(ev.target.checked ? "backup" : "delete")}/>
                </Tooltip>
            </Fieldset>
            <Box/>
            <Box/>
            <PresetSelector
                value={config.uploader_preset || "intan_rhs"}
                onChange={handlePresetChange}
                disabled={disabled}
            />
            <ExtensionInputs
                extensions={customExts}
                minAge={customAge}
                onExtensionsChange={handleExtensionsChange}
                onMinAgeChange={handleMinAgeChange}
                disabled={disabled}
            />
        </Box>
    );
};
